import { Add, Close, Remove } from '@mui/icons-material';
import {
  Box,
  Button as MuiButton,
  Divider,
  Fade,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button, theme } from '@theguarantors/tg-package-material-ui';

import type { ApplicantsPresentation, SectionConfig, TextFieldConfig } from '../../config';
import { collectFieldDefaults } from '../../lease-details.defaults';
import { LeaseTextInput } from '../../inputs/lease-text-input';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { FieldsRenderer } from '../../fields-renderer';
import { SectionShell } from '../section-shell';

// The only array-editor section in the engine; backed by RHF `useFieldArray`.
// Two layouts, auto-selected from the config:
//  - email-only (the only field is `email`) → the compact invite UI: stacked
//    email inputs, additional rows fade in with an inline remove adornment, and
//    an outlined "add" button.
//  - otherwise (email + name fields) → the detailed UI: per-entry headers and
//    full-width fields, shape driven by field `visible`.

export type ApplicantsSectionProps = {
  config: SectionConfig & ApplicantsPresentation;
};

const APPLICANTS_FIELD = 'applicants';

const defaultEntryTitle = (index: number) =>
  index === 0 ? 'Primary applicant' : `Additional applicant`;

export function ApplicantsSection({ config }: ApplicantsSectionProps) {
  const fieldKeys = Object.keys(config.fields);
  const isEmailOnly = fieldKeys.length === 1 && fieldKeys[0] === 'email';
  return isEmailOnly ? (
    <EmailOnlyApplicants config={config} />
  ) : (
    <DetailedApplicants config={config} />
  );
}

// Compact invite layout: email-only rows, inline remove adornment, outlined add.
function EmailOnlyApplicants({ config }: ApplicantsSectionProps) {
  const { control, getValues, getFieldState, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: APPLICANTS_FIELD });

  const emailField = config.fields.email as TextFieldConfig | undefined;
  const required = Boolean(emailField?.required);
  const onBlur = emailField?.events?.onBlur;
  const labels = config.compactEmailLabels ?? {};
  const fallbackLabel = typeof emailField?.label === 'string' ? emailField.label : '';

  const fireEmailBlur = (index: number, value: string | undefined) => {
    if (!onBlur) {
      return;
    }
    const path = `${APPLICANTS_FIELD}.${index}.email`;
    onBlur({
      value: value ?? '',
      valid: !getFieldState(path, formState).invalid,
      fieldPath: path,
      arrayIndex: index,
      sectionKey: 'applicants',
      getValues: (name?: string) => (name == null ? getValues() : getValues(name)),
    });
  };

  const handleAdd = () => {
    const index = fields.length;
    append({ ...config.defaultEntry, ...collectFieldDefaults(config.fields) });
    config.onAdd?.({ count: index + 1, index });
  };

  const handleRemove = (index: number) => {
    const count = fields.length - 1;
    remove(index);
    config.onRemove?.({ count, index });
  };

  // Leading rows (default 1 = primary) are locked: no Remove button.
  const lockedLeadingCount = config.lockedLeadingCount ?? 1;

  const renderRow = (index: number) => {
    const isPrimary = index === 0;
    // Rows before the locked count are locked (no Remove).
    const isLocked = index < lockedLeadingCount;
    // `lockedLeadingCount` is only set by renewal (its existing members are read-only). Defaults to
    // 0 otherwise, so `isReadOnly` is always false for the invite form.
    const isReadOnly = index < (config.lockedLeadingCount ?? 0);
    return (
      <LeaseTextInput
        name={`${APPLICANTS_FIELD}.${index}.email`}
        title={(isPrimary ? labels.primary : labels.additional) ?? fallbackLabel}
        required={required}
        disabled={isReadOnly}
        onBlurValue={onBlur ? (value) => fireEmailBlur(index, value) : undefined}
        endAdornment={
          isLocked ? undefined : (
            <IconButton
              aria-label={`remove applicant ${index + 1}`}
              onClick={() => handleRemove(index)}
              edge="end"
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          )
        }
      />
    );
  };

  return (
    <SectionShell
      title={config.title}
      subtitle={config.subtitle}
      slots={config.slots}
      spacing={config.spacing}
    >
      <Stack direction="column" spacing={2}>
        {fields.map((field, index) =>
          index === 0 ? (
            <Box key={field.id}>{renderRow(index)}</Box>
          ) : (
            <Fade in={true} key={field.id}>
              <Box>{renderRow(index)}</Box>
            </Fade>
          ),
        )}
        {fields.length < config.max && (
          // Copied verbatim from the legacy renter-contact "add" button (MUI
          // Button + styling), so the compact invite layout matches pixel-for-pixel.
          <MuiButton
            id="add-roommate-button"
            data-testid="add-roommate-button"
            variant="outlined"
            onClick={handleAdd}
            sx={{
              // `mt` omitted vs the legacy button — the enclosing Stack already
              // provides the 16px gap (legacy used `mt: 2` in a non-Stack layout).
              alignSelf: 'flex-start',
              padding: '12px',
              pl: 2,
              pr: 3,
              gap: 1,
              borderColor: `${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]} !important`,
              [theme.breakpoints.down('md')]: { width: 'fit-content' },
            }}
          >
            <Add />
            {config.addButtonLabel ?? 'Add applicant'}
          </MuiButton>
        )}
      </Stack>
    </SectionShell>
  );
}

// Detailed layout: per-entry headers + full-width fields (email + names).
function DetailedApplicants({ config }: ApplicantsSectionProps) {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: APPLICANTS_FIELD });
  const { applicantsSpacing } = useLeaseDetailsUI();

  const allowRemove = config.allowRemove ?? true;
  const entryTitle = config.entryTitle ?? defaultEntryTitle;
  const canAdd = fields.length < config.max;
  const canRemove = allowRemove && fields.length > config.min;

  // `primaryLocked` accepts three shapes — see `ApplicantsPresentation`:
  //   true                → every primary-row field is locked (cascade)
  //   false               → nothing locked
  //   { fields: [...] }   → only the listed field names locked on primary
  const primaryLockSpec = config.primaryLocked;
  const primaryLocksAllFields = primaryLockSpec === true;
  const primaryLockedFieldNames =
    primaryLockSpec && typeof primaryLockSpec === 'object'
      ? new Set(primaryLockSpec.fields)
      : undefined;
  const primaryHasAnyLock = primaryLocksAllFields || (primaryLockedFieldNames?.size ?? 0) > 0;

  const handleAdd = () => {
    const index = fields.length;
    append({ ...config.defaultEntry, ...collectFieldDefaults(config.fields) });
    config.onAdd?.({ count: index + 1, index });
  };

  const handleRemove = (index: number) => {
    const count = fields.length - 1;
    remove(index);
    config.onRemove?.({ count, index });
  };

  const entriesSpacing = applicantsSpacing?.entries ?? 3;
  const entrySpacing = applicantsSpacing?.entry ?? 2;

  return (
    <SectionShell
      title={config.title}
      subtitle={config.subtitle}
      slots={config.slots}
      spacing={config.spacing}
    >
      <Stack direction="column" spacing={entriesSpacing}>
        {fields.map((field, index) => {
          const isPrimary = index === 0;
          const showRemove = !isPrimary && canRemove;
          const inheritedDisabled = isPrimary && primaryLocksAllFields;
          const inheritedDisabledFieldNames =
            isPrimary && primaryLockedFieldNames ? primaryLockedFieldNames : undefined;
          const showLockedSubtitle = isPrimary && primaryHasAnyLock;
          return (
            <Box key={field.id}>
              {index > 0 && <Divider sx={{ mb: entriesSpacing }} />}
              <Stack direction="column" spacing={entrySpacing}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="body2"
                    component="h3"
                    sx={{
                      color: theme.palette.neutralGrey[950],
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {entryTitle(index)}
                  </Typography>
                  {showRemove ? (
                    <MuiButton
                      aria-label={`remove applicant ${index + 1}`}
                      id={`applicants-${index}-remove`}
                      onClick={() => handleRemove(index)}
                      type="button"
                      variant="text"
                      size="small"
                      startIcon={<Remove />}
                      sx={{
                        color: theme.palette.red[600],
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '22px',
                        height: '32px',
                        p: '5px 12px 5px 8px',
                        m: `0 !important`,
                      }}
                    >
                      Remove
                    </MuiButton>
                  ) : null}
                </Stack>
                <FieldsRenderer
                  fields={config.fields}
                  sectionKey="applicants"
                  arrayIndex={index}
                  inheritedDisabled={inheritedDisabled}
                  inheritedDisabledFieldNames={inheritedDisabledFieldNames}
                />
                {showLockedSubtitle && config.primaryLockedSubtitle ? (
                  <Typography variant="body2" sx={{ color: 'grey.600' }}>
                    {config.primaryLockedSubtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
        {canAdd ? (
          <Button
            id="applicants-add"
            startIcon={<Add />}
            onClick={handleAdd}
            variant="text"
            size="medium"
            color="primary"
            sx={{ alignSelf: 'flex-start' }}
          >
            {config.addButtonLabel ?? 'Add applicant'}
          </Button>
        ) : null}
      </Stack>
    </SectionShell>
  );
}
